import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  useEventCallback,
} from '@mui/material';
import { CloseIcon } from '@diligentcorp/atlas-react-icons';
import { mailToSupport } from '@issuemanager/constants';
import { paths } from '@issuemanager/apps/client/paths';
import { useMemo, JSX, ChangeEvent } from 'react';
import { useSourceParams } from '../../pages/issue/hooks/use-source-params';
import { generateCallbackUrl } from '../../pages/issue/new-issue/utils';

interface Props {
  title: string;
  description: string;
  isOpen: boolean;
  hasHomeButton: boolean;
  hasBackButton: boolean;
  handleClose: () => void;
}

export function ErrorDialog({
  title,
  description,
  isOpen,
  hasHomeButton = false,
  hasBackButton = false,
  handleClose,
}: Props): JSX.Element {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { sourceInfo } = useSourceParams();

  const showHomeButton = useMemo(
    () => hasHomeButton && !hasBackButton,
    [hasBackButton, hasHomeButton],
  );

  const showBackButton = useMemo(
    () => hasBackButton && !hasHomeButton,
    [hasBackButton, hasHomeButton],
  );

  const showCloseButton = useMemo(() => !showBackButton, [showBackButton]);

  const closeButtonVariant = useMemo(
    () => (showBackButton || showHomeButton ? 'text' : 'contained'),
    [showBackButton, showHomeButton],
  );

  const goHome = useEventCallback(() => {
    navigate(paths.app.index);
    handleClose();
  });

  const goBack = useEventCallback(() => {
    if (sourceInfo?.callbackUrl) {
      window.location.href = generateCallbackUrl({
        url: sourceInfo.callbackUrl,
        result: 'error',
      });
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(paths.app.index);
    }
    handleClose();
  });

  const onClose = (
    _event: ChangeEvent<HTMLInputElement>,
    reason: string,
  ): void => {
    const ignoredReasons = ['backdropClick', 'escapeKeyDown'];
    if (showBackButton && ignoredReasons.includes(reason)) {
      return;
    }
    handleClose();
  };

  return (
    <Dialog data-testid='app-error-dialog' open={isOpen} onClose={onClose}>
      <DialogTitle id='app-error-dialog-title'>
        {title}
        {showCloseButton && (
          <IconButton
            data-testid='close-button'
            aria-label='app-error-dialog-close'
            color='inherit'
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id='app-error-dialog-description'>
          <Trans
            i18nKey={description}
            components={{
              Link: <Link href={mailToSupport} />,
            }}
          />
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {showCloseButton && (
          <Button onClick={handleClose} variant={closeButtonVariant}>
            {t('form.closeButtonLabel')}
          </Button>
        )}
        {showHomeButton && (
          <Button
            data-testid='go-home-button'
            variant='contained'
            onClick={goHome}
          >
            {t('form.goHomeButtonLabel')}
          </Button>
        )}
        {showBackButton && (
          <Button
            data-testid='go-back-button'
            variant='contained'
            onClick={goBack}
          >
            {t('form.goBackButtonLabel')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
